// src/components/operationCenter/widgets/thesis/ThesisStatusBadge.tsx
// Status badge for thesis validation state

import React from "react";
import {
  CheckCircle,
  XCircle,
  PlayCircle,
  PauseCircle,
  FileEdit,
  Archive,
  Loader2,
} from "lucide-react";
import type { ThesisStatus } from "@/types/thesis";

interface ThesisStatusBadgeProps {
  status: ThesisStatus;
  size?: "sm" | "md";
  showLabel?: boolean;
}

const STATUS_CONFIG: Record<
  ThesisStatus,
  {
    label: string;
    icon: React.ElementType;
    className: string;
    bgClassName: string;
  }
> = {
  draft: {
    label: "Draft",
    icon: FileEdit,
    className: "text-slate-400",
    bgClassName: "bg-slate-500/20",
  },
  active: {
    label: "Active",
    icon: PlayCircle,
    className: "text-blue-400",
    bgClassName: "bg-blue-500/20",
  },
  validating: {
    label: "Validating",
    icon: Loader2,
    className: "text-amber-400",
    bgClassName: "bg-amber-500/20",
  },
  validated: {
    label: "Validated",
    icon: CheckCircle,
    className: "text-emerald-400",
    bgClassName: "bg-emerald-500/20",
  },
  invalidated: {
    label: "Invalidated",
    icon: XCircle,
    className: "text-red-400",
    bgClassName: "bg-red-500/20",
  },
  paused: {
    label: "Paused",
    icon: PauseCircle,
    className: "text-slate-400",
    bgClassName: "bg-slate-500/20",
  },
  archived: {
    label: "Archived",
    icon: Archive,
    className: "text-slate-500",
    bgClassName: "bg-slate-600/20",
  },
};

export const ThesisStatusBadge: React.FC<ThesisStatusBadgeProps> = ({
  status,
  size = "sm",
  showLabel = true,
}) => {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  const sizeClasses = {
    sm: {
      container: "px-1.5 py-0.5 gap-1",
      icon: "w-3 h-3",
      text: "text-[9px]",
    },
    md: {
      container: "px-2 py-1 gap-1.5",
      icon: "w-3.5 h-3.5",
      text: "text-[10px]",
    },
  };

  const sizes = sizeClasses[size];

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium uppercase tracking-wide
        ${sizes.container}
        ${config.className}
        ${config.bgClassName}
      `}
    >
      <Icon
        className={`${sizes.icon} ${status === "validating" ? "animate-spin" : ""}`}
      />
      {showLabel && <span className={sizes.text}>{config.label}</span>}
    </span>
  );
};

export default ThesisStatusBadge;
